import { useEffect } from 'react';

declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: any) => void;
    dataLayer: any[];
  }
}

const Analytics = () => {
  useEffect(() => {
    // Google Analytics 4 (GA4) Setup
    const GA_MEASUREMENT_ID = 'G-XXXXXXXXXX'; // Replace with your actual GA4 Measurement ID
    
    // Load Google Analytics script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script1);

    // Initialize dataLayer and gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: any[]) {
      window.dataLayer.push(args);
    };
    
    // Configure GA4
    window.gtag('js', new Date() as any);
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
      send_page_view: true,
      anonymize_ip: true, // GDPR compliance
      allow_google_signals: false, // Privacy-focused
      allow_ad_personalization_signals: false
    });

    // Google Search Console verification
    const gscMeta = document.createElement('meta');
    gscMeta.name = 'google-site-verification';
    gscMeta.content = 'YOUR_GOOGLE_SEARCH_CONSOLE_VERIFICATION_CODE'; // Replace with actual code
    document.head.appendChild(gscMeta);

    // Bing Webmaster Tools verification
    const bingMeta = document.createElement('meta');
    bingMeta.name = 'msvalidate.01';
    bingMeta.content = 'YOUR_BING_VERIFICATION_CODE'; // Replace with actual code
    document.head.appendChild(bingMeta);

    // Facebook domain verification
    const fbMeta = document.createElement('meta');
    fbMeta.name = 'facebook-domain-verification';
    fbMeta.content = 'YOUR_FACEBOOK_DOMAIN_VERIFICATION'; // Replace with actual code
    document.head.appendChild(fbMeta);

    return () => {
      // Cleanup
      document.head.removeChild(script1);
      document.head.removeChild(gscMeta);
      document.head.removeChild(bingMeta);
      document.head.removeChild(fbMeta);
    };
  }, []);

  // Track custom events
  const trackEvent = (eventName: string, parameters: any = {}) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        event_category: parameters.category || 'engagement',
        event_label: parameters.label || '',
        value: parameters.value || 0,
        ...parameters
      });
    }
  };

  // Expose tracking functions to window for global access
  useEffect(() => {
    (window as any).trackEvent = trackEvent;
    
    // Track scroll depth
    let maxScroll = 0;
    const handleScroll = () => {
      const scrollPercent = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100);
      if (scrollPercent > maxScroll && scrollPercent % 25 === 0) {
        maxScroll = scrollPercent;
        trackEvent('scroll_depth', {
          category: 'engagement',
          label: `${scrollPercent}%`,
          value: scrollPercent
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return null;
};

// Export tracking utilities
export const trackConversion = (conversionType: string, value?: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'G-XXXXXXXXXX/conversion', // Replace with your conversion tracking
      event_category: 'conversion',
      event_label: conversionType,
      value: value || 0
    });
  }
};

export const trackFormSubmission = (formName: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'form_submit', {
      event_category: 'form',
      event_label: formName
    });
  }
};

export const trackDownload = (fileName: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'file_download', {
      event_category: 'download',
      event_label: fileName
    });
  }
};

export const trackOutboundLink = (url: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'click', {
      event_category: 'outbound',
      event_label: url,
      transport_type: 'beacon'
    });
  }
};

export default Analytics;